import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  TableColumn,
} from '@backstage/core-components';
import { Chip } from '@material-ui/core';
import { EntityRefLink, EntityRefLinks, humanizeEntityRef } from '@backstage/plugin-catalog-react';
import {
  ApiEntityV1,
  ComponentEntityV1,
  ResourceEntityV1,
  ModuleEntityV1,
} from '@teamsystem/backstage-plugin-teamsystem-kinds-common';

import { DEFAULT_NAMESPACE, Entity } from '@backstage/catalog-model';

const createAPIModuleColumn = (): TableColumn<CatalogTableRow> => {
  return {
    title: 'Module',
    field: 'entity.spec.module',
    render: ({ entity }) => {
      const api = entity as ApiEntityV1;
      if (!api.spec.module) {
        return (<></>);
      }
      const moduleRef = 'module:'.concat(entity.metadata?.namespace || DEFAULT_NAMESPACE).concat('/').concat(api.spec.module);
      return (<EntityRefLink entityRef={moduleRef} defaultKind="module" />);
    },
  };
};

const createComponentModuleColumn = (): TableColumn<CatalogTableRow> => {
  return {
    title: 'Module',
    field: 'entity.spec.module',
    render: ({ entity }) => {
      const component = entity as ComponentEntityV1;
      if (!component.spec.module) {
        return (<></>);
      }
      const moduleRef = 'module:'.concat(entity.metadata?.namespace || DEFAULT_NAMESPACE).concat('/').concat(component.spec.module);
      return (<EntityRefLink entityRef={moduleRef} defaultKind="module" />);
    },
  };
};

const createResourceModuleColumn = (): TableColumn<CatalogTableRow> => {
  return {
    title: 'Module',
    field: 'entity.spec.module',
    render: ({ entity }) => {
      const resource = entity as ResourceEntityV1;
      if (!resource.spec.module) {
        return (<></>);
      }
      const moduleRef = 'module:'.concat(entity.metadata?.namespace || DEFAULT_NAMESPACE).concat('/').concat(resource.spec.module);
      return (<EntityRefLink entityRef={moduleRef} defaultKind="module" />);
    },
  };
};

const createModuleAreaColumn = (): TableColumn<CatalogTableRow> => {
  const formatContent = (entity: Entity): string => {
    return (
      entity.metadata?.title || humanizeEntityRef(entity)
    );
  };

  return {
    title: 'Area',
    field: 'entity.spec.area',
    width: 'auto',
    customSort({ entity: entity1 }, { entity: entity2 }) {
      return formatContent(entity1).localeCompare(formatContent(entity2));
    },
    render: ({ entity }) => {
      const module = entity as ModuleEntityV1;
      if (!module.spec.area) {
        return (<></>);
      }
      const moduleRef = 'area:'.concat(entity.metadata?.namespace || DEFAULT_NAMESPACE).concat('/').concat(module.spec.area);
      return (<EntityRefLink entityRef={moduleRef} defaultKind="area" />);
    },
  };
};

const createModuleFunctionalDomainColumn = (): TableColumn<CatalogTableRow> => {
  function formatContent(entity: Entity): string {
    return (
      entity.metadata?.title ||
        humanizeEntityRef(entity, {
          defaultKind: 'area',
        })
    );
  };

  return {
    title: 'Functional Domain',
    field: 'entity.spec.subarea',
    width: 'auto',
    customSort({ entity: entity1 }, { entity: entity2 }) {
      return formatContent(entity1).localeCompare(formatContent(entity2));
    },
    render: ({ entity }) => {
      const module = entity as ModuleEntityV1;
      if (!module.spec.subarea) {
        return (<></>);
      }
      const moduleRef = 'area:'.concat(entity.metadata?.namespace || DEFAULT_NAMESPACE).concat('/').concat(module.spec.subarea);
      return (<EntityRefLink entityRef={moduleRef} defaultKind="area" />);
    },
  };
};

const createModuleOwnerColumn = (): TableColumn<CatalogTableRow> => {
  return {
    title: 'Team',
    field: 'resolved.ownedByRelationsTitle',
    render: ({ resolved }) => {
      return (<EntityRefLinks
        entityRefs={resolved.ownedByRelations}
        defaultKind="group"
      />);
    },
  };
}

const createModuleLegacyTargetColumn = (): TableColumn<CatalogTableRow> => {
  return {
    title: 'Legacy/Target',
    width: 'auto',
    render: ({ entity }) => {
      const module = entity as ModuleEntityV1;
      return (
        <>
          {module.spec?.type && module.spec?.type === 'business capability' && <Chip key={module.metadata?.name} label='BC' size='small' style={{ backgroundColor: 'lightgreen' }} />}
          {module.spec?.target && module.spec?.target === true && <Chip key={module.metadata?.name} label='T' size='small' />}
          {module.spec?.legacy && module.spec?.legacy === true && <Chip key={module.metadata?.name} label='L' size='small' />}
        </>
      );      
    },
  };
};

export const catalogColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  const kind = entityListContext.filters.kind?.value ?? '';

  if (kind.toLowerCase() === 'area') {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createMetadataDescriptionColumn(),
    ];
  }

  if (kind.toLowerCase() === 'module') {
    return [
      createModuleAreaColumn(),
      createModuleFunctionalDomainColumn(),
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createMetadataDescriptionColumn(),
      createModuleOwnerColumn(),
      // CatalogTable.columns.createOwnerColumn(),
      // CatalogTable.columns.createSpecTypeColumn(),
      createModuleLegacyTargetColumn(),
    ];
  }

  if (kind.toLowerCase() === 'api') {
    return [
      CatalogTable.columns.createTitleColumn({ hidden: true }),
      CatalogTable.columns.createNameColumn({ defaultKind: kind }),
      createAPIModuleColumn(),
      CatalogTable.columns.createOwnerColumn(),
      CatalogTable.columns.createSpecTypeColumn(),
      CatalogTable.columns.createSpecLifecycleColumn(),
      CatalogTable.columns.createMetadataDescriptionColumn(),
      CatalogTable.columns.createTagsColumn(),
    ];
  }

  if (kind.toLowerCase() === 'component') {
    return [
      CatalogTable.columns.createTitleColumn({ hidden: true }),
      CatalogTable.columns.createNameColumn({ defaultKind: kind }),
      createComponentModuleColumn(),
      CatalogTable.columns.createOwnerColumn(),
      CatalogTable.columns.createSpecTypeColumn(),
      CatalogTable.columns.createSpecLifecycleColumn(),
      CatalogTable.columns.createMetadataDescriptionColumn(),
      CatalogTable.columns.createTagsColumn(),
    ];
  }

  if (kind.toLowerCase() === 'resource') {
    return [
      CatalogTable.columns.createTitleColumn({ hidden: true }),
      CatalogTable.columns.createNameColumn({ defaultKind: kind }),
      createResourceModuleColumn(),
      CatalogTable.columns.createOwnerColumn(),
      CatalogTable.columns.createSpecTypeColumn(),
      CatalogTable.columns.createSpecLifecycleColumn(),
      CatalogTable.columns.createMetadataDescriptionColumn(),
      CatalogTable.columns.createTagsColumn(),
    ];
  }

  return CatalogTable.defaultColumnsFunc(entityListContext);
};